import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { fetchCompanyTest, listarSets, type CompanyTest } from './setsApi';
import type { TestSet } from './tipos';
import SetEditor from './SetEditor';
import CorridaView from './CorridaView';
import CompararView from './CompararView';
import AnalisisView from './analisis/AnalisisView';

/**
 * Harness de test masivo de prompts (spec 2026-08-23). Ruta oculta /test-masivo,
 * SOLO cuentas dev_tester: sin link en el sidebar, y con redirect a home para
 * cualquier cuenta real que adivine la URL.
 */
type Tab = 'sets' | 'corridas' | 'comparar' | 'analisis';

const TestMasivo: React.FC = () => {
  const [company, setCompany] = useState<CompanyTest | null | 'cargando'>('cargando');
  const [sets, setSets] = useState<TestSet[]>([]);
  const [setActivo, setSetActivo] = useState<TestSet | null>(null);
  const [tab, setTab] = useState<Tab>('sets');

  useEffect(() => {
    fetchCompanyTest().then(setCompany).catch(() => setCompany(null));
  }, []);

  const recargarSets = async () => {
    const lista = await listarSets();
    setSets(lista);
    if (setActivo) setSetActivo(lista.find((s) => s.id === setActivo.id) ?? null);
  };

  useEffect(() => {
    if (company && company !== 'cargando' && company.devTester) void recargarSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  if (company === 'cargando') {
    return <div className="p-8 text-sm text-gray-500">Cargando…</div>;
  }
  if (!company || !company.devTester) return <Navigate to="/" replace />;

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'sets', label: 'Sets' },
    { id: 'corridas', label: 'Corridas' },
    { id: 'comparar', label: 'Comparar' },
    // Análisis de fidelidad generada-vs-producto (spec 2026-09-03): no necesita set.
    { id: 'analisis', label: 'Análisis' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-brand-black">Test masivo de prompts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Solo cuentas de prueba · corre contra el backend real y gasta créditos (te quedan{' '}
            <b className="text-brand-black">{company.imagenesRestantes}</b>)
          </p>
        </header>

        <nav className="mb-6 flex gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-full py-2 px-4 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'bg-brand-black text-white'
                  : 'bg-white text-brand-black border border-gray-200 hover:border-gray-400'
              }`}
            >
              {t.label}
            </button>
          ))}
          {setActivo && (
            <span className="ml-auto self-center text-sm text-gray-500">
              Set: <b className="text-brand-black">{setActivo.nombre}</b>
            </span>
          )}
        </nav>

        {tab === 'sets' && (
          <SetEditor
            company={company}
            sets={sets}
            setActivo={setActivo}
            onSeleccionar={(s) => setSetActivo(s)}
            onSetsChanged={recargarSets}
          />
        )}
        {tab === 'corridas' &&
          (setActivo ? (
            <CorridaView idCompany={company.idCompany} set={setActivo} imagenesRestantes={company.imagenesRestantes} />
          ) : (
            <ElegirSet />
          ))}
        {tab === 'comparar' && (setActivo ? <CompararView set={setActivo} idCompany={company.idCompany} /> : <ElegirSet />)}
        {tab === 'analisis' && <AnalisisView />}
      </div>
    </div>
  );
};

const ElegirSet: React.FC = () => (
  <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500">
    Elegí (o creá) un set en la pestaña <b>Sets</b> primero.
  </div>
);

export default TestMasivo;
